import React, { useEffect, useContext, useMemo } from 'react';
import first from 'lodash.first';
import delve from 'dlv';

import { PromiseState } from 'react-refetch';

import getRadioKPIUrl from 'url-constructors/getRadioKPIUrl';
import getRadioTrendUrl from 'url-constructors/getRadioTrendUrl';
import getRadioSpinsURL from 'url-constructors/getRadioSpinsURL';

import salesTypes from 'maps/sales-types';

import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';

import OverviewContainer from 'components/Overview/Container/Container';
import RadioPerformanceKPIs from './RadioKPIs';
import TrendChart from '../shared/TrendChart';

import { Grid } from 'components/Overview/Container/shared.style';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import RadioTable from './RadioTable';
import metrics from 'maps/metrics';
import { TimeFrames } from 'components/TopicFilters/shared';

function dataSources(): any {
  return {
    fetchRadioSpinsData: (args: any): any => ({
      radioSpinsData: getRadioSpinsURL(args),
    }),
    fetchTrendData: (args: any): any => ({
      trendData: getRadioTrendUrl(args),
    }),
    fetchKPIData: (args: any): any => ({
      kpiData: getRadioKPIUrl(args),
    }),
  };
}

function getLocalSortBy(globalSortBy: TimeFrames): string {
  switch (globalSortBy) {
    case 'lw':
      return 'countLastWeek';
    case 'twtd':
    default:
      return 'countThisWeekToDate';
  }
}

interface IRadioPerformanceProps {
  fetchRadioSpinsData: Function;
  fetchTrendData: Function;
  fetchKPIData: Function;
  radioSpinsData: PromiseState<any>;
  kpiData: PromiseState<any>;
  trendData: PromiseState<any>;
}

/*
 * The radio performance card shows a trend line from the last 30 days and
 * a breakdown table of the top countries
 *
 * Note: as the `sortBy` parameter differs per endpoint, the global sort metric
 * is converted to one supported by this data container.
 */
const RadioPerformance: React.FC<IRadioPerformanceProps> = (props) => {
  const {
    fetchRadioSpinsData,
    fetchTrendData,
    fetchKPIData,
    radioSpinsData = PromiseState.create(),
    trendData = PromiseState.create(),
    kpiData = PromiseState.create(),
  } = props;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    filters,
    viewSettings,
    compareMode,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';
  const countryFilter = filters?.country ?? undefined;
  const regionFilter = filters?.region ?? undefined;

  const activeMetric =
    timeframe === 'twtd' ? 'countThisWeekToDate' : 'countLastWeek';

  const sortByValue = getLocalSortBy(timeframe);
  const sortByMetric = metrics[sortByValue];

  const kpis: any = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const value = delve(kpis, ['data', sortByMetric.value], 0);
  const change = sortByMetric.change
    ? delve(kpis, ['data', sortByMetric.change], 0)
    : 0;

  const secondTableHeader =
    timeframe === 'twtd' ? 'this week to date' : 'last full week';

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      compareMode,
    };
    fetchKPIData({
      ...args,
      countryIds: countryFilter,
      region: regionFilter,
      customRegion: customRegionFilter,
    });
    fetchTrendData({
      ...args,
      period: 'day',
      countryIds: countryFilter,
      region: regionFilter,
      customRegion: customRegionFilter,
    });
    fetchRadioSpinsData({
      ...args,
      limit: 5,
      spinsType: 'countries',
      sortBy: activeMetric,
    });
  }, [
    entityIds,
    entityType,
    fetchKPIData,
    fetchTrendData,
    fetchRadioSpinsData,
    activeMetric,
    countryFilter,
    regionFilter,
    compareMode,
    customRegionFilter,
  ]);

  const [, isPending] = useCombinedRefetchState([
    kpiData,
    trendData,
    radioSpinsData,
  ]);

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Radio Performance"
      subtitle={salesTypes.radio.label}
      linkTo={useUrlForTopic('radio')}
    >
      <Grid>
        <div>
          <RadioPerformanceKPIs
            value={value}
            change={change}
            isPending={isPending}
            title={sortByMetric.label}
          />
          <TrendChart trendData={trendData} isPending={isPending} />
        </div>
        <RadioTable
          activeMetric={activeMetric}
          headers={['top countries', secondTableHeader]}
          radioSpinsData={radioSpinsData.value}
          isPending={isPending}
          filteredCountryId={countryFilter}
        />
      </Grid>
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(RadioPerformance as any);
